// src/modules/agentic/RAGLab.jsx
// RAG Lab — chunking · embedding · retrieval · augmentation · evaluation · advanced RAG

import React, { useState, useRef, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#22d3ee';
import { _livePalette as P } from '../../context/paletteSync';

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── FAKE EMBEDDING + COSINE ──────────────────────────────────────────────────
const embed = (text, seed=0) => {
  const h = text.split('').reduce((s,c,i)=>s+c.charCodeAt(0)*(i+1)*(seed+1), seed*13+7);
  return Array.from({length:12}, (_,i) => Math.tanh(Math.sin(h*0.07+i*1.37+seed*0.3)*2.5));
};
const cosine = (a,b) => {
  const dot=a.reduce((s,v,i)=>s+v*b[i],0);
  return dot/(Math.sqrt(a.reduce((s,v)=>s+v*v,0))*Math.sqrt(b.reduce((s,v)=>s+v*v,0))+1e-8);
};

// ─── SAMPLE DOCUMENT ─────────────────────────────────────────────────────────
const SAMPLE_DOC = `Transformer Architecture and Attention Mechanisms

The transformer architecture was introduced in the paper "Attention Is All You Need" (Vaswani et al., 2017). It relies entirely on self-attention mechanisms, dispensing with recurrence and convolutions.

Self-attention computes relationships between all positions in a sequence simultaneously. For each token, queries Q, keys K, and values V are computed as linear projections. The attention score is: Attention(Q,K,V) = softmax(QK^T / sqrt(d_k)) * V.

Multi-head attention runs h parallel attention functions, allowing the model to attend to information from different representation subspaces. The outputs are concatenated and linearly projected.

Positional encoding adds sequence order information since attention is permutation-invariant. Sine and cosine functions of different frequencies encode position.

The feed-forward network applies two linear transformations with a ReLU activation in between. Layer normalization and residual connections stabilize training.

BERT uses the transformer encoder for masked language modeling. GPT uses the decoder with causal masking for autoregressive generation. T5 uses both encoder and decoder for sequence-to-sequence tasks.

Scaling transformers with more parameters and data leads to emergent capabilities. GPT-3 with 175B parameters showed few-shot learning. GPT-4 extended this further with multimodal inputs.

Fine-tuning pre-trained transformers on downstream tasks requires far less data than training from scratch. RLHF (Reinforcement Learning from Human Feedback) aligns model outputs with human preferences.`;

// ─── CHUNKING ────────────────────────────────────────────────────────────────
const chunkDoc = (text, size, overlap) => {
  const words = text.split(/\s+/);
  const chunks = [];
  let i=0;
  while(i < words.length) {
    const chunk = words.slice(i, i+size).join(' ');
    if(chunk.trim()) chunks.push({ id: chunks.length, text: chunk, wordStart: i, wordEnd: Math.min(i+size, words.length) });
    i += size - overlap;
  }
  return chunks;
};

// ─── RAG PIPELINE SVG ────────────────────────────────────────────────────────
function RAGPipelineSVG({ stage }) {
  const W=700, H=110;
  const stages = [
    {label:'DOCUMENTS',   sub:'raw corpus',        color:'#64748b', icon:'📄'},
    {label:'CHUNK',       sub:'split text',         color:'#22d3ee', icon:'✂'},
    {label:'EMBED',       sub:'vectorize',          color:'#4ade80', icon:'🔢'},
    {label:'INDEX',       sub:'vector DB',          color:'#a78bfa', icon:'🗄'},
    {label:'QUERY',       sub:'user question',      color:'#fbbf24', icon:'❓'},
    {label:'RETRIEVE',    sub:'top-K similar',      color:'#fb923c', icon:'🔍'},
    {label:'AUGMENT',     sub:'context + question', color:'#f87171', icon:'📋'},
    {label:'GENERATE',    sub:'LLM answer',         color:'#22d3ee', icon:'🧠'},
  ];
  const sw=(W-20)/stages.length;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {stages.map((s,i)=>{
        const x=10+i*sw, active=stage===i||stage===-1;
        const isCurrent = stage===i;
        return (
          <g key={s.label}>
            <rect x={x+2} y={18} width={sw-6} height={62} rx={4}
              fill={isCurrent?s.color+'28':active?s.color+'0a':'transparent'}
              stroke={s.color} strokeWidth={isCurrent?2:0.8} opacity={active?1:0.3}/>
            <text x={x+sw/2} y={37} textAnchor="middle" style={{fontSize:'14px'}}>{s.icon}</text>
            <text x={x+sw/2} y={54} textAnchor="middle" style={{fontSize:'8px',fill:s.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{s.label}</text>
            <text x={x+sw/2} y={66} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{s.sub}</text>
            {i<stages.length-1&&<line x1={x+sw-2} y1={49} x2={x+sw+2} y2={49} stroke={s.color} strokeWidth={1.5} opacity={0.5}/>}
          </g>
        );
      })}
      <text x={W/2} y={H-4} textAnchor="middle" style={{fontSize:'8px',fill:stage>=0?stages[stage]?.color:'#475569',fontFamily:'JetBrains Mono'}}>
        {stage>=0?`Stage ${stage+1}/8: ${stages[stage]?.label}`:'RAG Pipeline — click SIMULATE to walk through'}
      </text>
    </svg>
  );
}

// ─── EVALUATION METRICS ───────────────────────────────────────────────────────
const evalRAG = (chunkSize, overlap, topK, embedQuality) => {
  const retrievalScore = Math.min(0.98, 0.5 + topK*0.04 + embedQuality*0.3 - chunkSize*0.001);
  const contextQuality = Math.min(0.99, 0.6 + (1-chunkSize/500)*0.2 + (overlap/chunkSize)*0.15 + embedQuality*0.1);
  const faithfulness   = Math.min(0.99, 0.7 + retrievalScore*0.15 + contextQuality*0.1);
  const relevance      = Math.min(0.99, retrievalScore*0.6 + contextQuality*0.35 + 0.05);
  return {
    retrievalScore: +retrievalScore.toFixed(3),
    contextQuality: +contextQuality.toFixed(3),
    faithfulness:   +faithfulness.toFixed(3),
    relevance:      +relevance.toFixed(3),
    ragas:          +((retrievalScore+contextQuality+faithfulness+relevance)/4).toFixed(3),
  };
};

const TABS = ['pipeline','chunking','retrieval','augmentation','evaluation','advanced'];

export default function RAGLab() {
  const [tab, setTab] = useState('pipeline');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'RAG Lab — Retrieval-Augmented Generation: from raw docs to grounded answers.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [pipeStage, setPipeStage] = useState(-1);
  const [pipeRunning, setPipeRunning] = useState(false);
  const pipeRef = useRef();

  const [chunkSize, setChunkSize] = useState(100);
  const [overlap, setOverlap]     = useState(20);
  const [chunks, setChunks]       = useState([]);
  const [selectedChunk, setSelectedChunk] = useState(null);

  const [query, setQuery] = useState('How does self-attention work?');
  const [topK, setTopK]   = useState(3);
  const [embedQuality, setEmbedQuality] = useState(0.8);
  const [retrieved, setRetrieved] = useState([]);

  const runPipeline = () => {
    if(pipeRunning){ clearInterval(pipeRef.current); setPipeRunning(false); setPipeStage(-1); return; }
    setPipeStage(0); setPipeRunning(true);
    let i=0;
    const stages=['DOCUMENTS','CHUNK','EMBED','INDEX','QUERY','RETRIEVE','AUGMENT','GENERATE'];
    pipeRef.current = setInterval(()=>{
      i++; setPipeStage(i);
      log(`Stage ${i}: ${stages[Math.min(i,7)]}`,'info');
      if(i>=7){ clearInterval(pipeRef.current); setPipeRunning(false); log('RAG pipeline complete — answer generated','ok'); }
    }, 700);
  };

  const genChunks = () => {
    const c = chunkDoc(SAMPLE_DOC, chunkSize, overlap);
    setChunks(c);
    setSelectedChunk(null);
    log(`Chunked into ${c.length} chunks | size=${chunkSize} words, overlap=${overlap}`,'ok');
  };

  const runRetrieval = () => {
    if(!chunks.length){ log('Generate chunks first!','warn'); return; }
    const qEmbed = embed(query, 99);
    const scored = chunks.map(c=>({
      ...c,
      embedding: embed(c.text, c.id),
      sim: 0,
    })).map(c=>({...c, sim:+((cosine(qEmbed,c.embedding)+1)/2).toFixed(4)}))
      .sort((a,b)=>b.sim-a.sim);
    setRetrieved(scored);
    log(`Retrieved top-${topK} chunks for: "${query.slice(0,40)}"`,'ok');
  };

  useEffect(()=>{ genChunks(); },[chunkSize, overlap]);
  useEffect(()=>()=>clearInterval(pipeRef.current),[]);

  const metrics = evalRAG(chunkSize, overlap, topK, embedQuality);

  // Chunking strategy comparison
  const strategyData = [
    {strategy:'Fixed 50w',   recall:0.61, precision:0.72, speed:0.95, cost:0.90},
    {strategy:'Fixed 100w',  recall:0.72, precision:0.78, speed:0.93, cost:0.85},
    {strategy:'Fixed 200w',  recall:0.80, precision:0.74, speed:0.90, cost:0.75},
    {strategy:'Sentence',    recall:0.79, precision:0.83, speed:0.82, cost:0.80},
    {strategy:'Paragraph',   recall:0.85, precision:0.80, speed:0.80, cost:0.70},
    {strategy:'Semantic',    recall:0.92, precision:0.90, speed:0.55, cost:0.55},
    {strategy:'Recursive',   recall:0.88, precision:0.86, speed:0.70, cost:0.65},
  ];

  // Advanced RAG techniques data
  const advancedTechs = [
    {name:'HyDE',color:'#22d3ee',desc:'Hypothetical Document Embeddings: generate a fake answer first, embed it, retrieve similar real docs. Better semantic match.',gain:'+12% recall'},
    {name:'Multi-Query',color:'#4ade80',desc:'Rephrase query N ways, run N retrievals, union results. Reduces sensitivity to query wording.',gain:'+8% recall'},
    {name:'RAG-Fusion',color:'#a78bfa',desc:'Multiple queries + Reciprocal Rank Fusion to rerank results. Combines strengths of all retrievals.',gain:'+15% precision'},
    {name:'Contextual Compression',color:'#fbbf24',desc:'Retrieved chunks are too large? LLM extracts only the relevant sentences before injecting into context.',gain:'-40% tokens'},
    {name:'Self-RAG',color:'#fb923c',desc:'Model decides when to retrieve vs answer from memory. Generates [Retrieve] tokens as signals. More efficient.',gain:'+9% faithfulness'},
    {name:'Corrective RAG',color:'#f87171',desc:'Evaluates retrieval quality. If poor → web search fallback. Never answers from bad context.',gain:'+18% accuracy'},
  ];

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── PIPELINE ── */}
      {tab==='pipeline'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="RAG PIPELINE — 8 STAGES FROM DOCUMENT TO ANSWER" accent={AC}>
            <RAGPipelineSVG stage={pipeStage}/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
              <Btn onClick={runPipeline} color={pipeRunning?'#f87171':AC} style={{flex:1}}>{pipeRunning?'STOP':'▶ SIMULATE PIPELINE'}</Btn>
              <Btn onClick={()=>{clearInterval(pipeRef.current);setPipeRunning(false);setPipeStage(-1);}} color="#64748b">RESET</Btn>
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="INDEXING PHASE (offline, run once)" accent="#4ade80">
              {[
                {step:'1. Ingest documents', detail:'PDFs, HTML, markdown, code, databases → raw text'},
                {step:'2. Clean & preprocess', detail:'Remove noise, fix encoding, extract structured content'},
                {step:'3. Chunk', detail:'Split into pieces the model can reason about (~100-500 tokens)'},
                {step:'4. Embed', detail:'Each chunk → dense vector via embedding model (text-embedding-3, BGE, E5)'},
                {step:'5. Index', detail:'Store vectors in ANN index (FAISS, Pinecone, Weaviate, pgvector)'},
              ].map(({step,detail},i)=>(
                <div key={i} style={{display:'flex',gap:'10px',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px'}}>
                  <span style={{fontSize:'9px',color:'#4ade80',fontFamily:'JetBrains Mono',fontWeight:700,minWidth:110}}>{step}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{detail}</span>
                </div>
              ))}
            </Panel>
            <Panel title="QUERY PHASE (online, per request)" accent="#fb923c">
              {[
                {step:'6. Embed query', detail:'User question → same embedding model → query vector'},
                {step:'7. ANN search', detail:'Find top-K chunks by cosine similarity in milliseconds'},
                {step:'8. Rerank (opt)', detail:'Cross-encoder reranks top-K for better precision'},
                {step:'9. Augment prompt', detail:'Inject retrieved chunks before user question in context'},
                {step:'10. Generate', detail:'LLM reads context + question → grounded answer'},
              ].map(({step,detail},i)=>(
                <div key={i} style={{display:'flex',gap:'10px',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px'}}>
                  <span style={{fontSize:'9px',color:'#fb923c',fontFamily:'JetBrains Mono',fontWeight:700,minWidth:110}}>{step}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{detail}</span>
                </div>
              ))}
            </Panel>
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}

      {/* ── CHUNKING ── */}
      {tab==='chunking'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
            <Panel title="CHUNK CONFIG" accent={AC}>
              <Sl label="Chunk size (words)" value={chunkSize} min={20} max={300} step={10} onChange={setChunkSize} color={AC}/>
              <Sl label="Overlap (words)" value={overlap} min={0} max={Math.floor(chunkSize/2)} step={5} onChange={setOverlap} color="#4ade80"/>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px',marginTop:'4px'}}>
                <Metric label="Chunks" value={chunks.length} color={AC}/>
                <Metric label="Overlap %" value={`${((overlap/chunkSize)*100).toFixed(0)}%`} color="#4ade80"/>
                <Metric label="Avg words" value={chunkSize} color="#a78bfa"/>
                <Metric label="Coverage" value="100%" color="#fbbf24"/>
              </div>
            </Panel>
            <Panel title={`CHUNKS (${chunks.length} total) — click to inspect`} accent={AC}>
              <div style={{display:'flex',flexWrap:'wrap',gap:'4px',maxHeight:200,overflowY:'auto',marginBottom:'8px'}}>
                {chunks.map((c,i)=>(
                  <button key={c.id} onClick={()=>setSelectedChunk(c)} style={{padding:'4px 8px',background:selectedChunk?.id===c.id?AC+'28':'var(--deep)',border:`1px solid ${selectedChunk?.id===c.id?AC:'var(--b0)'}`,color:selectedChunk?.id===c.id?AC:'var(--t3)',fontSize:'8px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>
                    #{c.id}
                  </button>
                ))}
              </div>
              {selectedChunk&&(
                <div style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderLeft:`2px solid ${AC}`}}>
                  <div style={{fontSize:'9px',color:AC,fontFamily:'JetBrains Mono',fontWeight:700,marginBottom:'4px'}}>Chunk #{selectedChunk.id} · words {selectedChunk.wordStart}–{selectedChunk.wordEnd}</div>
                  <div style={{fontSize:'9px',color:'var(--t2)',lineHeight:1.7}}>{selectedChunk.text}</div>
                </div>
              )}
            </Panel>
          </div>
          <Panel title="CHUNKING STRATEGY COMPARISON — RECALL vs PRECISION vs COST" accent="#4ade80">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={strategyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="strategy" tick={{fontSize:8,fill:'var(--t3)'}}/>
                <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                <Tooltip/><Legend/>
                <Bar dataKey="recall"    fill="#22d3ee" name="Recall"    radius={[2,2,0,0]}/>
                <Bar dataKey="precision" fill="#4ade80" name="Precision" radius={[2,2,0,0]}/>
                <Bar dataKey="cost"      fill="#f87171" name="Cost eff." radius={[2,2,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}

      {/* ── RETRIEVAL ── */}
      {tab==='retrieval'&&(
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="RETRIEVAL CONFIG" accent={AC}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',letterSpacing:'.1em'}}>QUERY</div>
              <input value={query} onChange={e=>setQuery(e.target.value)}
                style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'8px',fontSize:'10px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',marginBottom:'10px',boxSizing:'border-box'}}/>
              <Sl label="Top K" value={topK} min={1} max={8} step={1} onChange={setTopK} color={AC}/>
              <Sl label="Embed quality (sim)" value={embedQuality} min={0.3} max={1.0} step={0.05} onChange={setEmbedQuality} color="#4ade80"/>
              <Btn onClick={runRetrieval} color={AC} style={{width:'100%',marginTop:'4px'}}>RETRIEVE →</Btn>
            </Panel>
            <Panel title="SIMILARITY METRICS" accent="#a78bfa">
              {[
                {name:'Cosine',formula:'dot(a,b)/(|a||b|)',use:'Direction — normalized vectors'},
                {name:'Dot product',formula:'Σ aᵢbᵢ',use:'Magnitude + direction'},
                {name:'Euclidean',formula:'√Σ(aᵢ-bᵢ)²',use:'Geometric distance'},
                {name:'BM25',formula:'TF-IDF variant',use:'Sparse keyword matching'},
              ].map(({name,formula,use})=>(
                <div key={name} style={{marginBottom:'5px',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',borderLeft:'2px solid #a78bfa'}}>
                  <span style={{fontSize:'9px',color:'#a78bfa',fontWeight:700,display:'block'}}>{name}</span>
                  <span style={{fontSize:'8px',color:'var(--t3)',fontFamily:'JetBrains Mono'}}>{formula} — {use}</span>
                </div>
              ))}
            </Panel>
          </div>
          <Panel title={`COSINE SIMILARITY RESULTS — "${query.slice(0,45)}"`} accent={AC}>
            <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
              {(retrieved.length ? retrieved : chunks.slice(0,8)).map((c,i)=>{
                const sim = c.sim ?? 0.5;
                const isTop = i < topK && retrieved.length;
                return (
                  <div key={c.id} style={{padding:'8px 10px',background:'var(--deep)',borderRadius:'3px',border:`1px solid ${isTop?P[i]+'44':'var(--b0)'}`,opacity:retrieved.length&&!isTop?0.4:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'4px'}}>
                      <span style={{fontSize:'8px',color:'var(--t3)',fontFamily:'JetBrains Mono',minWidth:40}}>#{c.id}</span>
                      <div style={{flex:1,height:6,background:'var(--b0)',borderRadius:2,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${sim*100}%`,background:isTop?P[i]:'#334155',borderRadius:2,transition:'width .3s'}}/>
                      </div>
                      <span style={{fontSize:'9px',color:isTop?P[i]:'var(--t3)',fontFamily:'JetBrains Mono',fontWeight:700,minWidth:40,textAlign:'right'}}>{(sim*100).toFixed(1)}%</span>
                      {isTop&&<span style={{fontSize:'8px',color:P[i],fontWeight:700,minWidth:40}}>TOP {i+1}</span>}
                    </div>
                    <div style={{fontSize:'9px',color:'var(--t2)',lineHeight:1.5}}>{c.text.slice(0,120)}…</div>
                  </div>
                );
              })}
              {!retrieved.length&&<div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',textAlign:'center'}}>← Set query and click RETRIEVE to see results</div>}
            </div>
          </Panel>
        </div>
      )}

      {/* ── AUGMENTATION ── */}
      {tab==='augmentation'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="PROMPT AUGMENTATION — INJECTING CONTEXT INTO THE LLM PROMPT" accent="#fb923c">
            <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
              {[
                {label:'SYSTEM',color:'#a78bfa',content:'You are a helpful AI assistant. Answer questions using ONLY the provided context. If the answer is not in the context, say "I don\'t know."'},
                {label:'CONTEXT [1]',color:'#22d3ee',content:(retrieved[0]||chunks[0])?.text || 'Self-attention computes relationships between all positions in a sequence simultaneously. For each token, queries Q, keys K, and values V are computed as linear projections...'},
                {label:'CONTEXT [2]',color:'#22d3ee',content:(retrieved[1]||chunks[1])?.text || 'Multi-head attention runs h parallel attention functions, allowing the model to attend to information from different representation subspaces...'},
                {label:'CONTEXT [3]',color:'#22d3ee',content:(retrieved[2]||chunks[2])?.text || 'Positional encoding adds sequence order information since attention is permutation-invariant...'},
                {label:'USER QUERY',color:'#fbbf24',content:query},
                {label:'LLM ANSWER',color:'#4ade80',content:'Based on the provided context: Self-attention computes Q, K, V projections from each token, then computes attention scores as softmax(QK^T / √d_k) × V. This allows each position to attend to all other positions simultaneously, capturing long-range dependencies without recurrence.'},
              ].map(({label,color,content})=>(
                <div key={label} style={{padding:'8px 12px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${color}`}}>
                  <div style={{fontSize:'8px',color,fontFamily:'JetBrains Mono',fontWeight:700,marginBottom:'4px',letterSpacing:'.1em'}}>{label}</div>
                  <div style={{fontSize:'9px',color:'var(--t2)',lineHeight:1.6}}>{content}</div>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="WHY RAG BEATS FINE-TUNING FOR KNOWLEDGE TASKS" accent="#4ade80">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {aspect:'Fine-tuning',issues:['Knowledge baked in at training time','Expensive to update ($$$)','Prone to hallucination on edge cases','Can\'t cite sources'],color:'#f87171'},
                {aspect:'RAG',issues:['Knowledge retrieved at query time','Cheap to update (add docs to DB)','Grounded in retrieved text','Can cite exact source chunks'],color:'#4ade80'},
                {aspect:'RAG + Fine-tuning',issues:['Best of both worlds','FT for style/format','RAG for fresh knowledge','State of the art production setup'],color:'#22d3ee'},
              ].map(({aspect,issues,color})=>(
                <div key={aspect} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'6px'}}>{aspect}</div>
                  {issues.map((issue,i)=><div key={i} style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px',paddingLeft:'8px',borderLeft:`1px solid ${color}44`}}>{issue}</div>)}
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── EVALUATION ── */}
      {tab==='evaluation'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'260px 1fr',gap:'12px'}}>
            <Panel title="TUNE PARAMETERS" accent={AC}>
              <Sl label="Chunk size (words)" value={chunkSize} min={20} max={300} step={10} onChange={setChunkSize} color={AC}/>
              <Sl label="Overlap" value={overlap} min={0} max={80} step={5} onChange={setOverlap} color="#4ade80"/>
              <Sl label="Top K" value={topK} min={1} max={10} step={1} onChange={setTopK} color="#a78bfa"/>
              <Sl label="Embed quality" value={embedQuality} min={0.3} max={1.0} step={0.05} onChange={setEmbedQuality} color="#fbbf24"/>
              <div style={{marginTop:'8px',padding:'8px',background:'var(--deep)',borderRadius:'3px',borderLeft:'2px solid #fbbf24'}}>
                <div style={{fontSize:'9px',color:'#fbbf24',fontWeight:700,marginBottom:'4px'}}>RAGAS Score</div>
                <div style={{fontSize:'22px',color:'#fbbf24',fontFamily:'Syne',fontWeight:800}}>{metrics.ragas}</div>
              </div>
            </Panel>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                <Metric label="Retrieval" value={metrics.retrievalScore} color="#22d3ee"/>
                <Metric label="Context quality" value={metrics.contextQuality} color="#4ade80"/>
                <Metric label="Faithfulness" value={metrics.faithfulness} color="#a78bfa"/>
                <Metric label="Relevance" value={metrics.relevance} color="#fbbf24"/>
              </div>
              <Panel title="RAGAS METRICS OVER CHUNK SIZE" accent="#fbbf24">
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={[20,50,100,150,200,300].map(cs=>({cs,...evalRAG(cs,Math.floor(cs*0.2),topK,embedQuality)}))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="cs" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'chunk size',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                    <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0.5,1.0]}/>
                    <Tooltip/><Legend/>
                    <Line type="monotone" dataKey="retrievalScore" stroke="#22d3ee" strokeWidth={2} dot={false} name="Retrieval"/>
                    <Line type="monotone" dataKey="faithfulness"   stroke="#a78bfa" strokeWidth={2} dot={false} name="Faithfulness"/>
                    <Line type="monotone" dataKey="relevance"      stroke="#fbbf24" strokeWidth={2} dot={false} name="Relevance"/>
                    <Line type="monotone" dataKey="ragas"          stroke="#4ade80" strokeWidth={2.5} dot={false} name="RAGAS"/>
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
            </div>
          </div>
          <Panel title="RAGAS EVALUATION FRAMEWORK" accent="#a78bfa">
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
              {[
                {metric:'Context Recall',desc:'Are all relevant answer facts present in retrieved context?',formula:'relevant chunks / total relevant chunks',color:'#22d3ee'},
                {metric:'Context Precision',desc:'Are retrieved chunks actually relevant to the question?',formula:'relevant chunks / total retrieved',color:'#4ade80'},
                {metric:'Faithfulness',desc:'Does the answer stay within the retrieved context? No hallucination.',formula:'correct claims / total claims',color:'#a78bfa'},
                {metric:'Answer Relevance',desc:'Does the answer actually address the question asked?',formula:'cosine(answer, question)',color:'#fbbf24'},
              ].map(({metric,desc,formula,color})=>(
                <div key={metric} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'4px'}}>{metric}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6,marginBottom:'5px'}}>{desc}</div>
                  <div style={{fontSize:'8px',color:color,fontFamily:'JetBrains Mono',padding:'4px',background:'var(--b0)',borderRadius:'2px'}}>{formula}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── ADVANCED ── */}
      {tab==='advanced'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
            {advancedTechs.map(t=>(
              <Panel key={t.name} title={t.name} accent={t.color}>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7,marginBottom:'6px'}}>{t.desc}</div>
                <div style={{padding:'4px 8px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${t.color}`,fontSize:'9px',color:t.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{t.gain}</div>
              </Panel>
            ))}
          </div>
          <Panel title="NAIVE RAG vs ADVANCED RAG vs MODULAR RAG" accent={AC}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'9px'}}>
              <thead><tr style={{background:'var(--deep)'}}>
                {['Aspect','Naive RAG','Advanced RAG','Modular RAG'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',borderBottom:'1px solid var(--b0)',fontSize:'9px'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {[
                  ['Query','Raw user question','HyDE / Multi-query','Query routing to best pipeline'],
                  ['Chunking','Fixed size','Semantic / recursive','Adaptive per doc type'],
                  ['Embedding','Single model','Fine-tuned domain model','Ensemble models'],
                  ['Retrieval','Cosine sim top-K','Hybrid (dense+sparse)','Multi-step: retrieve→rerank→verify'],
                  ['Reranking','None','Cross-encoder rerank','Task-specific reranker'],
                  ['Augmentation','Paste chunks','Compress + summarize','Dynamic context assembly'],
                  ['Evaluation','Manual spot-check','RAGAS metrics','Continuous auto-eval in prod'],
                ].map((row,i)=>(
                  <tr key={row[0]} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'var(--deep)':'transparent'}}>
                    {row.map((cell,j)=>(
                      <td key={j} style={{padding:'6px 10px',color:j===0?'var(--t3)':j===1?'#64748b':j===2?'#22d3ee':'#4ade80',fontWeight:j===0?700:400,fontFamily:j>0?'JetBrains Mono':'inherit',fontSize:'9px'}}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
